import { ReactNode, useEffect, useMemo } from 'react';

import { MODULE, ScheduleDTO } from '@gearbelt/global';
import { Button } from '@gearbelt/ui/components';
import { ColumnFiltersState, RowSelectionState } from '@tanstack/react-table';

import manholeShrugVector from '@/assets/vectors/manhole-shrug.svg';
import schedulesVector from '@/assets/vectors/schedules.svg';
import UndoIcon from '@/components/icons/undo';
import { TablePageTable } from '@/components/table-page/table-page-table';
import { usePermissions } from '@/hooks/use-permissions';
import { useModulesStore } from '@/stores/modules.store';
import { useWorkspaceNavigate } from '@/utils/routes';

import { createSchedulesTableColumns } from './table-meta';

export const SchedulesTable = ({
  notFoundDisplay,
  data,
  loading,
  onRowSelectionChange,
  rowSelection,
  columnFilters,
  onColumnFiltersChange,
  sorting,
  grouping,
  columnOrder,
  columnVisibility,
  onSortingChange,
}: {
  notFoundDisplay: ReactNode;
  data: (ScheduleDTO & { assetCount: number; checkCount: number })[];
  loading: boolean;
  onRowSelectionChange: (selectedRows: RowSelectionState) => void;
  rowSelection: RowSelectionState;
  columnFilters: ColumnFiltersState;
  onColumnFiltersChange: (columnFilters: ColumnFiltersState) => void;
  sorting: { key?: string; direction?: 'asc' | 'desc' };
  grouping: string[];
  columnOrder: Array<{ id: string; active: boolean }>;
  columnVisibility: Record<string, boolean>;
  onSortingChange: (sorting: { key?: string; direction?: 'asc' | 'desc' }) => void;
}) => {
  const navigate = useWorkspaceNavigate();
  const { syncModuleColumns } = useModulesStore();
  const { canManageSchedules } = usePermissions();
  const columns = createSchedulesTableColumns({ canManageSchedules });

  const initialSorting = useMemo(
    () =>
      sorting.key
        ? [
            {
              id: sorting.key,
              desc: sorting.direction === 'desc',
            },
          ]
        : [],
    [sorting.key, sorting.direction],
  );

  useEffect(() => {
    syncModuleColumns(MODULE.Schedules, createSchedulesTableColumns());
  }, [syncModuleColumns]);

  return (
    <div className="flex-1 bg-white">
      <TablePageTable<ScheduleDTO & { assetCount: number; checkCount: number }>
        data={data}
        columns={columns}
        notFoundDisplay={notFoundDisplay}
        loading={loading}
        onSortingChange={onSortingChange}
        initialSorting={initialSorting}
        grouping={grouping}
        columnOrder={(columnOrder || []).map((column) => column.id)}
        onRowClick={(row) => {
          // /schedules/:id is the editor — Members can't create or edit
          // schedules per matrix, so don't route them into it.
          if (!canManageSchedules) return;
          navigate(`/schedules/${row.id}`);
        }}
        rowSelection={rowSelection}
        onRowSelectionChange={onRowSelectionChange}
        columnVisibility={columnVisibility}
        columnFilters={columnFilters}
        onColumnFiltersChange={onColumnFiltersChange}
      />
    </div>
  );
};

export const NoSchedulesCreatedDisplay = () => {
  return (
    <div className="absolute inset-0 m-auto flex w-[383px] flex-col items-center justify-center gap-[20px]">
      <img src={schedulesVector} alt="Schedules" className="h-[170px]" />
      <span className="text-xl leading-[30px] font-medium">No schedules have been created yet</span>
      <div className="flex flex-col gap-0 text-center">
        <span className="text-secondary-text text-sm">Create schedules and deploy them to assets to track their services, checks and tests.</span>
      </div>
    </div>
  );
};

export function NoSchedulesFoundFromSearchDisplay() {
  return (
    <div className="absolute inset-0 flex h-full w-full flex-col items-center justify-center gap-4 p-4">
      <img src={manholeShrugVector} alt="Forms" className="h-[170px]" />
      <span className="text-sm">No schedules match your search</span>
    </div>
  );
}

export function NoSchedulesFoundFromFiltersDisplay({ removeColumnFilters }: { removeColumnFilters: () => void }) {
  return (
    <div className="bg-grey-1 absolute inset-0 flex h-full w-full flex-col items-center justify-center gap-4 p-4">
      <img src={manholeShrugVector} alt="Schedules" className="h-[170px]" />
      <span className="text-xl font-medium">No schedules match your filters</span>
      <span className="text-secondary-text text-sm">Try adjusting your column filters.</span>
      <div className="pointer-events-auto flex gap-2">
        <Button variant="grey" onClick={removeColumnFilters} className="gap-1 font-normal">
          <UndoIcon />
          Remove all filters
        </Button>
      </div>
    </div>
  );
}
